import { PortalProvider } from '../../hooks';
import { isShowLoader, selectBottomSheets, selectModalWindows, selectStatus, useAppSelector } from '../../store';
import { Portal } from '../../foundation';
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react';
import Widget from '../../widgets/Widget';
import { makeWidgetsList } from '../../widgets/WidgetsList';
import { BottomSheet } from '../Popup/BottomSheet';
import { ModalWindow } from '../Popup/ModalWindow';
import { Popup } from '../Popup/Popup';
import { ToastBar } from '../ToastBar/ToastBar';
import { PageWrapper } from './PageWrapper';

const WidgetsList = makeWidgetsList(Widget);

export const PortalWrapper = ({ children }: { children: React.ReactNode }) => {
    const bottomSheets = useAppSelector(selectBottomSheets);
    const modalWindows = useAppSelector(selectModalWindows);
    const status = useAppSelector(selectStatus);
    const isLoading = useAppSelector(isShowLoader);
    const bsInternalIdForOpen = useMemo(() => Object.keys(bottomSheets).find((bsInternalId) => bottomSheets[bsInternalId].isOpen), [bottomSheets]);
    const [modal, setModal] = useState<React.ReactNode>(undefined);
    const [bottomSheet, setBottomSheet] = useState<string | undefined>();
    const [modalWindow, setModalWindow] = useState<string | undefined>();
    const [popup, setPopup] = useState<React.ReactNode>(undefined);

    const contextValue = useMemo(
        () => ({
            setModal,
            setPopup,
            setBottomSheet,
            setModalWindow,
        }),
        [],
    );

    const updateBottomSheet = useEffectEvent((loading: boolean, bshId?: string) => {
        if (loading) {
            setBottomSheet(undefined);
        } else if (bshId) {
            setBottomSheet(bshId);
        }
    });

    useEffect(() => {
        updateBottomSheet(isLoading, bsInternalIdForOpen);
        return function cleanup() {
            setBottomSheet(undefined);
        };
    }, [bsInternalIdForOpen, isLoading]);

    return (
        <PageWrapper>
            <PortalProvider value={contextValue}>
                {children}
                <BottomSheet
                    widgetId={bottomSheet}
                    status={status}
                    modal={modal}
                    setBottomSheet={setBottomSheet}
                    bottomSheets={bottomSheets}
                    WidgetsList={WidgetsList}
                />
                <ModalWindow widgetId={modalWindow} status={status} modalWindows={modalWindows} WidgetsList={WidgetsList} setModalWindow={setModalWindow} />
                <Popup content={popup} modal={modal} setPopup={setPopup} />
                <ToastBar />
                {modal && <Portal>{modal}</Portal>}
            </PortalProvider>
        </PageWrapper>
    );
};
